import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import PrivateImage from '../../components/common/PrivateImage';
import {
  ArrowLeft,
  UserCircle,
  Trophy,
  Target,
  Clock,
  Activity,
  TrendingUp,
  Calendar,
  AlertTriangle
} from 'lucide-react';

const PlayerStats = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [trainingStats, setTrainingStats] = useState(null);
  const [matchStats, setMatchStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPlayerStats();
  }, [id]);

  const fetchPlayerStats = async () => {
    try {
      setLoading(true);

      // Cargar jugador
      const { data: playerData, error: playerError } = await supabase
        .from('players')
        .select('*')
        .eq('id', id)
        .single();

      if (playerError) throw playerError;
      setPlayer(playerData);

      // Cargar estadísticas de entrenamientos
      const { data: trainingData, error: trainingError } = await supabase
        .from('player_training_stats')
        .select('*')
        .eq('player_id', id)
        .single();

      if (!trainingError) {
        setTrainingStats(trainingData);
      }

      // Cargar estadísticas de partidos
      const { data: matchData, error: matchError } = await supabase
        .from('player_match_stats')
        .select('*')
        .eq('player_id', id)
        .single();

      if (!matchError) {
        setMatchStats(matchData);
      }

      // Cargar alertas activas
      const { data: alertsData, error: alertsError } = await supabase
        .from('player_alerts')
        .select('*')
        .eq('player_id', id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (!alertsError) {
        setAlerts(alertsData || []);
      }

    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      setError('Error al cargar las estadísticas del jugador');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'alta': return 'bg-red-100 text-red-800 border-red-200';
      case 'media': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'baja': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      ausencias_repetidas: 'Ausencias Repetidas',
      sobrecarga: 'Sobrecarga Física',
      riesgo_lesion: 'Riesgo de Lesión',
      sancion: 'Sanción',
      rendimiento_bajo: 'Rendimiento Bajo',
      otro: 'Otro'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || 'Jugador no encontrado'}</p>
          <button
            onClick={() => navigate('/players')}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Volver a Jugadores
          </button>
        </div>
      </div>
    );
  }

  const totalMinutes = (trainingStats?.total_minutes || 0) + (matchStats?.total_minutes || 0);
  const totalActivities = (trainingStats?.total_trainings || 0) + (matchStats?.total_matches || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/players/${id}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Estadísticas del Jugador</h1>
              <p className="text-sm text-gray-600">
                {player.first_name} {player.last_name} - {player.team}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Alertas activas */}
        {alerts.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Alertas Activas
            </h2>
            <div className="space-y-3">
              {alerts.map(alert => (
                <div
                  key={alert.id}
                  className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold">{getAlertTypeLabel(alert.alert_type)}</h3>
                      <p className="text-sm mt-1">{alert.description}</p>
                      <p className="text-xs mt-2 opacity-75">
                        {new Date(alert.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded">
                      {alert.severity.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Resumen general */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Resumen General
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{totalActivities}</div>
              <p className="text-sm text-gray-600 mt-1">Total actividades</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{totalMinutes}</div>
              <p className="text-sm text-gray-600 mt-1">Minutos totales</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{matchStats?.total_goals || 0}</div>
              <p className="text-sm text-gray-600 mt-1">Goles</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">{matchStats?.total_assists || 0}</div>
              <p className="text-sm text-gray-600 mt-1">Asistencias</p>
            </div>
          </div>
        </div>

        {/* Estadísticas de entrenamientos */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Entrenamientos
          </h2>
          {trainingStats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-600">Total entrenamientos</p>
                <p className="text-2xl font-bold text-gray-900">{trainingStats.total_trainings || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Minutos entrenados</p>
                <p className="text-2xl font-bold text-gray-900">{trainingStats.total_minutes || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">RPE promedio</p>
                <p className="text-2xl font-bold text-gray-900">
                  {trainingStats.avg_rpe ? parseFloat(trainingStats.avg_rpe).toFixed(1) : '-'} / 10
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Actitud promedio</p>
                <p className="text-2xl font-bold text-gray-900">
                  {trainingStats.avg_attitude ? parseFloat(trainingStats.avg_attitude).toFixed(1) : '-'} / 5
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Esfuerzo promedio</p>
                <p className="text-2xl font-bold text-gray-900">
                  {trainingStats.avg_effort ? parseFloat(trainingStats.avg_effort).toFixed(1) : '-'} / 5
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No hay datos de entrenamientos disponibles</p>
          )}
        </div>

        {/* Estadísticas de partidos */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Partidos
          </h2>
          {matchStats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-600">Partidos jugados</p>
                <p className="text-2xl font-bold text-gray-900">{matchStats.total_matches || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Minutos jugados</p>
                <p className="text-2xl font-bold text-gray-900">{matchStats.total_minutes || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Goles</p>
                <p className="text-2xl font-bold text-green-600">{matchStats.total_goals || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Asistencias</p>
                <p className="text-2xl font-bold text-blue-600">{matchStats.total_assists || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Tarjetas amarillas</p>
                <p className="text-2xl font-bold text-yellow-600">{matchStats.total_yellow_cards || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Tarjetas rojas</p>
                <p className="text-2xl font-bold text-red-600">{matchStats.total_red_cards || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">MVP</p>
                <p className="text-2xl font-bold text-amber-600">{matchStats.mvp_count || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Valoración media</p>
                <p className="text-2xl font-bold text-gray-900">
                  {matchStats.avg_performance ? parseFloat(matchStats.avg_performance).toFixed(1) : '-'} / 10
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No hay datos de partidos disponibles</p>
          )}
        </div>

        {/* Ratios y promedios */}
        {matchStats && matchStats.total_matches > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Ratios y Promedios
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-600">Goles por partido</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(matchStats.total_goals / matchStats.total_matches).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Asistencias por partido</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(matchStats.total_assists / matchStats.total_matches).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Minutos por partido</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Math.round(matchStats.total_minutes / matchStats.total_matches)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerStats;
